import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import { exportToPdf, getHistories } from '../services/historyService';
import { getScoreColorHex } from '../utils/scoreUtils';
import { marketingPalette, marketingSymbols } from '../content/marketing';
import {
  publicBodyFontFamily,
  publicHeadingFontFamily,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

const palette = marketingPalette;
const ITEMS_PER_PAGE = 10;

const surfaceSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: '26px', md: '30px' },
  border: `1px solid ${alpha(palette.ink, 0.08)}`,
  bgcolor: alpha('#ffffff', 0.94),
  boxShadow: '0 28px 72px rgba(18, 61, 103, 0.08)',
};

const metaPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.6,
  px: 1.15,
  py: 0.55,
  borderRadius: '999px',
  bgcolor: alpha(palette.heroBlue, 0.08),
  color: palette.inkSoft,
  fontSize: '0.84rem',
  fontWeight: 700,
  fontFamily: publicBodyFontFamily,
};

const paginationSx = {
  '& .MuiPaginationItem-root': {
    fontFamily: publicBodyFontFamily,
    fontWeight: 700,
    color: palette.ink,
    borderRadius: '14px',
  },
  '& .Mui-selected': {
    bgcolor: `${alpha(palette.heroBlueMid, 0.14)} !important`,
    color: `${palette.heroBlueDeep} !important`,
  },
};

function formatDate(dateString) {
  if (!dateString) return 'Date unavailable';

  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationMinutes(minutes) {
  const numeric = Number(minutes);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric);
}

function CasesMetaPill({ icon, children }) {
  return (
    <Box sx={metaPillSx}>
      {icon}
      <Box component="span">{children}</Box>
    </Box>
  );
}

function HistoriesListPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 1 });
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortValue, setSortValue] = useState('completedAt-desc');
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 320);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    let active = true;

    const fetchCases = async () => {
      try {
        setLoading(true);
        setError('');

        const queryParams = new URLSearchParams({
          sortBy: sortValue,
          search: searchQuery,
        });

        const response = await getHistories(page, ITEMS_PER_PAGE, queryParams.toString());

        if (!active) {
          return;
        }

        setCases(Array.isArray(response?.histories) ? response.histories : []);
        setPagination({
          page: response?.pagination?.page ?? page,
          limit: response?.pagination?.limit ?? ITEMS_PER_PAGE,
          total: response?.pagination?.total ?? 0,
          pages: response?.pagination?.pages ?? 1,
        });
      } catch (fetchError) {
        console.error('Error fetching cases:', fetchError);
        if (active) {
          setError('Failed to load cases. Please try again.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchCases();

    return () => {
      active = false;
    };
  }, [page, searchQuery, sortValue]);

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSortValue(event.target.value);
    setPage(1);
  };

  const handleExportPdf = async (id) => {
    try {
      setActionError('');
      await exportToPdf(id);
    } catch (exportError) {
      console.error('Error exporting case PDF:', exportError);
      if (exportError.response?.status === 402) {
        setActionError('You need additional credits to export this PDF.');
        return;
      }

      setActionError('Failed to export the PDF. Please try again.');
    }
  };

  const resultsLabel = useMemo(() => {
    if (loading) {
      return 'Loading cases...';
    }

    if (!pagination.total) {
      return searchQuery ? 'No matching cases found.' : 'No completed cases yet.';
    }

    const suffix = pagination.total === 1 ? 'case' : 'cases';
    return `${pagination.total} ${suffix}`;
  }, [loading, pagination.total, searchQuery]);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 3,
          p: { xs: 3, sm: 3.5, md: 4.5 },
          borderRadius: { xs: '28px', md: '34px' },
          background:
            'linear-gradient(140deg, rgba(18, 61, 103, 0.98) 0%, rgba(21, 83, 129, 0.97) 54%, rgba(49, 182, 228, 0.93) 100%)',
          color: palette.white,
          boxShadow: '0 34px 84px rgba(18, 61, 103, 0.16)',
        }}
      >
        <Box
          component="img"
          src={marketingSymbols.silhouette}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            right: { xs: -10, md: 10 },
            top: { xs: -18, md: -32 },
            width: { xs: 140, md: 220 },
            opacity: 0.14,
            pointerEvents: 'none',
          }}
        />

        <Stack spacing={1.25} sx={{ position: 'relative', zIndex: 1, maxWidth: 760 }}>
          <Chip
            icon={<DescriptionRoundedIcon sx={{ color: 'inherit !important' }} />}
            label="Completed consultations"
            sx={{
              alignSelf: 'flex-start',
              bgcolor: alpha('#ffffff', 0.12),
              color: palette.white,
              borderRadius: '999px',
              fontWeight: 800,
              fontFamily: publicBodyFontFamily,
            }}
          />
          <Typography
            component="h1"
            sx={{
              fontFamily: publicHeadingFontFamily,
              fontSize: { xs: '2.2rem', md: '3rem' },
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
            }}
          >
            Cases
          </Typography>
          <Typography
            sx={{
              maxWidth: 680,
              color: alpha('#ffffff', 0.84),
              fontSize: { xs: '1rem', md: '1.08rem' },
              lineHeight: 1.7,
              fontFamily: publicBodyFontFamily,
            }}
          >
            Review completed consultations, revisit feedback, and export reports when you need them.
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ ...surfaceSx, p: { xs: 2.25, md: 3 }, mb: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search by case title"
              value={searchInput}
              onChange={handleSearchChange}
              sx={publicTextFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: palette.inkSoft }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 280 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '18px',
                  bgcolor: alpha('#ffffff', 0.96),
                  fontFamily: publicBodyFontFamily,
                  fontWeight: 700,
                  '& fieldset': {
                    borderColor: alpha(palette.ink, 0.10),
                  },
                },
              }}
            >
              <Select value={sortValue} onChange={handleSortChange} displayEmpty startAdornment={(
                <InputAdornment position="start" sx={{ ml: 0.5, mr: 1 }}>
                  <SortRoundedIcon sx={{ color: palette.inkSoft, fontSize: 20 }} />
                </InputAdornment>
              )}
              >
                <MenuItem value="completedAt-desc">Newest first</MenuItem>
                <MenuItem value="completedAt-asc">Oldest first</MenuItem>
                <MenuItem value="overallPercentage-desc">Highest score</MenuItem>
                <MenuItem value="overallPercentage-asc">Lowest score</MenuItem>
                <MenuItem value="title-asc">Title A-Z</MenuItem>
                <MenuItem value="title-desc">Title Z-A</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
          >
            <Typography
              sx={{
                color: palette.inkSoft,
                fontWeight: 700,
                fontFamily: publicBodyFontFamily,
              }}
            >
              {resultsLabel}
            </Typography>

            {searchQuery ? (
              <Button
                onClick={() => {
                  setSearchInput('');
                  setPage(1);
                }}
                sx={{
                  minHeight: 40,
                  px: 2,
                  color: palette.heroBlueDeep,
                  textTransform: 'none',
                  fontWeight: 800,
                  fontFamily: publicBodyFontFamily,
                  borderRadius: '999px',
                  bgcolor: alpha(palette.heroBlue, 0.10),
                  '&:hover': {
                    bgcolor: alpha(palette.heroBlue, 0.18),
                  },
                }}
              >
                Clear search
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      {actionError ? (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: '20px',
            border: `1px solid ${alpha('#b42318', 0.12)}`,
          }}
        >
          {actionError}
        </Alert>
      ) : null}

      {error ? (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: '20px',
            border: `1px solid ${alpha('#b42318', 0.12)}`,
          }}
        >
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box
          sx={{
            ...surfaceSx,
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: palette.heroBlueMid }} />
          <Typography sx={{ color: palette.inkSoft, fontFamily: publicBodyFontFamily }}>
            Loading your cases...
          </Typography>
        </Box>
      ) : cases.length > 0 ? (
        <>
          <Stack spacing={2}>
            {cases.map((history) => {
              const duration = formatDurationMinutes(history.duration);
              const score = Number(history.overallPercentage) || 0;
              const scoreColor = getScoreColorHex(score);

              return (
                <Box key={history._id} sx={{ ...surfaceSx, p: { xs: 1.75, md: 2 } }}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={{ xs: 1.5, md: 2 }}
                    justifyContent="space-between"
                    alignItems={{ md: 'center' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          mb: 0.85,
                          pr: { md: 1.25 },
                          color: palette.ink,
                          fontFamily: publicHeadingFontFamily,
                          fontSize: { xs: '1.15rem', md: '1.28rem' },
                          lineHeight: 1.16,
                          letterSpacing: '-0.03em',
                          wordBreak: 'break-word',
                        }}
                      >
                        {history.title || 'Untitled case'}
                      </Typography>

                      <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                        <CasesMetaPill icon={<CalendarMonthRoundedIcon sx={{ fontSize: 18, color: palette.heroBlueMid }} />}>
                          {formatDate(history.completedAt)}
                        </CasesMetaPill>
                        {duration !== null ? (
                          <CasesMetaPill icon={<TimerRoundedIcon sx={{ fontSize: 18, color: palette.heroBlueMid }} />}>
                            {duration} min
                          </CasesMetaPill>
                        ) : null}
                      </Stack>
                    </Box>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.25}
                      alignItems={{ sm: 'center' }}
                      sx={{ width: { xs: '100%', md: 'auto' } }}
                    >
                      <Chip
                        label={`${score.toFixed(1)}%`}
                        sx={{
                          bgcolor: alpha(scoreColor, 0.14),
                          color: scoreColor,
                          borderRadius: '999px',
                          fontWeight: 800,
                          fontFamily: publicBodyFontFamily,
                          height: 31,
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          flexShrink: 0,
                        }}
                      />
                      <Button
                        component={RouterLink}
                        to={`/history/${history._id}`}
                        endIcon={<ArrowOutwardRoundedIcon />}
                        sx={{
                          ...publicPrimaryButtonSx,
                          minHeight: 42,
                          px: 2.05,
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        Review
                      </Button>
                      <Button
                        onClick={() => handleExportPdf(history._id)}
                        startIcon={<DownloadRoundedIcon />}
                        sx={{
                          ...publicSecondaryButtonSx,
                          minHeight: 42,
                          px: 1.8,
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        PDF
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          {pagination.pages > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5 }}>
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={(_, value) => setPage(value)}
                sx={paginationSx}
              />
            </Box>
          ) : null}
        </>
      ) : (
        <Box
          sx={{
            ...surfaceSx,
            py: { xs: 6, md: 8 },
            px: { xs: 2.5, md: 4 },
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2,
              borderRadius: '24px',
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(palette.heroBlue, 0.12),
              color: palette.heroBlueDeep,
            }}
          >
            <FileCopyOutlinedIcon sx={{ fontSize: 34 }} />
          </Box>
          <Typography
            sx={{
              mb: 1,
              color: palette.ink,
              fontFamily: publicHeadingFontFamily,
              fontSize: { xs: '1.5rem', md: '1.9rem' },
              letterSpacing: '-0.03em',
            }}
          >
            {searchQuery ? 'No matching cases' : 'No cases yet'}
          </Typography>
          <Typography
            sx={{
              maxWidth: 540,
              mx: 'auto',
              color: palette.inkSoft,
              lineHeight: 1.7,
              fontFamily: publicBodyFontFamily,
            }}
          >
            {searchQuery
              ? `No completed cases match "${searchQuery}". Try a broader title search.`
              : 'Completed consultations will appear here once they have been marked and saved to your account.'}
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default HistoriesListPage;
